import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import moment from 'moment';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import styles from '@components-clean/core/drag-drop-lists-core/drag-drop-item/style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import RagComponent from '@components-clean/core/rag-component/rag-component';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { ObservationType } from '@interfaces-clean/observations.interface';
import { NamedItemType } from '@interfaces-clean/organisationLov.interface';
import { StudyReviewLog } from '@interfaces-clean/review-logs.interface';
import editDialogStyles from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/controls/review-logs/style.module.scss';
import { ObservationDetails } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation-details';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';
import OrganisationLovApi from '@apis/bff-api/central-monitoring/organisationLovs';

interface Props {
    reviewLog: StudyReviewLog;
    studyRef: string | null;
    onSubmit: (data: EditReviewLogForm) => void;
    onClose: () => void;
}

export interface EditReviewLogForm {
    dataset: Date | null;
    association: string | null;
    panel: string | null;
    decision: { id: number; name: string; groupedBy: string; disabled: boolean; active: boolean } | null;
    comments: string | null;
    created_date: Date | null;
    created_by: string;
    updated_date: Date | null;
    updated_by: string | null;
    reviewLogId: number;
}

const COMPONENT_NAME = 'edit-study-review-log-dialog';

const EditStudyReviewLogDialog = ({ reviewLog, onClose, onSubmit, studyRef }: Props) => {
    const { t } = useTranslation('cm');

    const [decisionLov, setDecisionLov] = useState<
        { id: number; name: string; groupedBy: string; disabled: boolean }[]
    >([]);

    const { data: observation, isLoading: loadingObservation } = ObservationsApi.useGetDetails(
        studyRef || '',
        reviewLog.observation_id,
        ObservationType.Study,
    );

    const { data: lovData, isLoading: loadingData } = OrganisationLovApi.useGet(
        studyRef ?? '',
        NamedItemType.ReviewLogDecision,
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<EditReviewLogForm>({
        mode: 'all',
        defaultValues: {
            dataset: reviewLog.dataset ? new Date(reviewLog.dataset) : null,
            association: reviewLog.association ?? '',
            panel: reviewLog.panel ?? '',
            decision: reviewLog.decision ? { name: reviewLog.decision, id: reviewLog.decision_id } : null,
            comments: reviewLog.comments ?? '',
            created_date: reviewLog.created_date ? new Date(reviewLog.created_date) : null,
            created_by: reviewLog.created_by ?? '',
            updated_date: reviewLog.updatedDate ? new Date(reviewLog.updated_date) : null,
            updated_by: reviewLog.updated_by ?? '',
        },
    });

    useEffect(() => {
        if (lovData && !loadingData) {
            const decisionData = lovData
                .filter((y) => y && y.name)
                .sort((a, b) => {
                    // Sort active items first (true values before false values)
                    if (a?.active !== b?.active) {
                        return a?.active ? -1 : 1;
                    }

                    if (a.name && b.name) {
                        return a.name?.localeCompare(b.name);
                    }

                    return 0;
                })
                .map((data) => ({
                    name: data?.name ?? '',
                    id: data?.id ?? 0,
                    groupedBy: data?.active ? 'Active' : 'Inactive',
                    disabled: reviewLog.decision
                        ? reviewLog.decision.toLowerCase() === data.name?.toLowerCase()
                            ? false
                            : !data.active
                        : !data.active,
                    active: data?.active ?? false,
                }));

            setDecisionLov(decisionData);

            if (reviewLog.decision && reviewLog.decision_id) {
                const currentData = decisionData?.find(
                    (decisionDataList) => decisionDataList?.id === Number(reviewLog.decision_id),
                );
                if (currentData)
                    reset((currentDefaults) => {
                        return { ...currentDefaults, decision: currentData };
                    });
            }
        }
    }, [lovData]);

    const dialogTitle = `${t('common:edit')} ${t('reviewLogDialog.studyTitle')}: ${reviewLog.record_id}`;
    const primaryBtnLabel = t('common:update');
    const createdBy = `${moment(reviewLog?.created_date).format('DD-MMM-YYYY')} by ${reviewLog?.created_by}`;
    const updatedBy = `${moment(reviewLog?.updated_date).format('DD-MMM-YYYY')} by ${reviewLog?.updated_by}`;

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    return (
        <DraggableDialog
            dataTestId="edit-study-review-log-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            createdUpdatedInfo={<CreatedUpdatedInfo created={createdBy} updated={updatedBy} width={500} />}
            createdUpdatedBackgroundColour="gray"
            initialHeight={reviewLog.is_scatter_plot === 'Yes' ? 792 : 610}
            initialWidth={612}
            additionalInfo={
                <ObservationDetails
                    observation={observation}
                    observationLoading={loadingObservation}
                    type={ObservationType.Study}
                />
            }
            additionalInfoWidth={361}
            additionalInfoBtnLabel={t('observation')}
            bodyPadding="none"
            setHeight="100%"
        >
            <div
                className={cn(
                    reviewLog.is_scatter_plot === 'Yes' ? 'k-pt-md' : style.editSiteObservationDialog,
                    style.rightBorderStyle,
                    'k-w-full',
                    'k-h-full',
                )}
            >
                {reviewLog.is_scatter_plot === 'Yes' && (
                    <div className={cn('k-d-flex k-grid-cols-4 k-pb-sm')}>
                        <div className="k-flex-1 k-d-flex k-align-items-center k-justify-content-center">
                            <RagComponent
                                color="Red"
                                changeDirection={reviewLog.red_count_change_direction}
                                previousValue={reviewLog.previous_red_count}
                                currentValue={reviewLog.red_count}
                                numberFormat="decimal"
                                label={t('reviewLogDialog.redSites')}
                            />
                        </div>
                        <div className="k-flex-1 k-d-flex k-align-items-center k-justify-content-center">
                            <RagComponent
                                color="Amber"
                                changeDirection={reviewLog.amber_count_change_direction}
                                previousValue={reviewLog.previous_amber_count}
                                currentValue={reviewLog.amber_count}
                                numberFormat="decimal"
                                label={t('reviewLogDialog.amberSites')}
                            />
                        </div>
                        <div className="k-flex-1 k-d-flex k-align-items-center k-justify-content-center">
                            <RagComponent
                                color="Green"
                                changeDirection={reviewLog.green_count_change_direction}
                                previousValue={reviewLog.previous_green_count}
                                currentValue={reviewLog.green_count}
                                numberFormat="decimal"
                                label={t('reviewLogDialog.greenSites')}
                            />
                        </div>
                        <div className="k-flex-1 k-d-flex k-align-items-center k-justify-content-center">
                            <RagComponent
                                color="Purple"
                                changeDirection={reviewLog.purple_count_change_direction}
                                previousValue={reviewLog.previous_purple_count}
                                currentValue={reviewLog.purple_count}
                                numberFormat="decimal"
                                label={t('reviewLogDialog.purpleSites')}
                            />
                        </div>
                    </div>
                )}
                {/* study rate / study site mean / x volume / y volume */}
                {reviewLog.is_scatter_plot === 'Yes' && (
                    <div className={cn('', 'k-pl-md k-pr-md k-mt-1')}>
                        {/* ROW 1: x volume / study rate */}
                        <div
                            className={cn(
                                'k-d-flex k-flex-row k-pt-0.5 k-pb-1 k-gap-3 k-pb-2',
                                editDialogStyles.borderBottom,
                            )}
                        >
                            {/* R1 COL left: x-axis */}
                            <div
                                className={cn(
                                    '!k-basis-8/12 k-d-flex k-flex-col k-align-items-center',
                                    editDialogStyles.borderRight,
                                )}
                            >
                                {/* x axis title */}
                                <div className={cn(style.wordBreak, 'k-pr-3')}>{reviewLog.x_axis_title}</div>
                                {/* x-axis values */}
                                <div className={cn(reviewLog.previous_x_volume ? 'k-gap-3' : '', 'k-d-flex')}>
                                    {/* previous x value (if any) */}
                                    <div className={cn(editDialogStyles.readonlyPanelText)}>
                                        {reviewLog.previous_x_volume}
                                    </div>
                                    {/* direction indicator */}
                                    {reviewLog.x_volume_change_direction && (
                                        <div className={cn(editDialogStyles.readonlyPanelText)}>
                                            <FontAwesomeIcon
                                                fontSize="10px"
                                                icon={
                                                    reviewLog.x_volume_change_direction === '2'
                                                        ? ['far', 'hyphen']
                                                        : ['fass', 'triangle']
                                                }
                                                className={cn({
                                                    [styles.sortAscIcon]: reviewLog.x_volume_change_direction === '0',
                                                    [`${styles.sortDescIcon} k-rotate-180`]:
                                                        reviewLog.x_volume_change_direction === '1',
                                                })}
                                            />
                                        </div>
                                    )}
                                    {/* current x value */}
                                    <div className={cn(editDialogStyles.readonlyPanelText)}>{reviewLog.x_volume}</div>
                                </div>
                            </div>
                            {/* R1 COL right: study rate */}
                            <div className="!k-basis-4/12 k-d-flex k-flex-col k-align-items-center">
                                <div>{t('admin:STUDY_RATE')}</div>
                                <div className={cn(reviewLog.previous_study_rate ? 'k-gap-3' : '', 'k-d-flex')}>
                                    {/* previous value */}
                                    {reviewLog.previous_study_rate && (
                                        <div className={cn(editDialogStyles.readonlyPanelText)}>
                                            {reviewLog.previous_study_rate}
                                        </div>
                                    )}
                                    {/* direction indicator */}
                                    {reviewLog.study_rate_change_direction && (
                                        <div className={cn(editDialogStyles.readonlyPanelText)}>
                                            <FontAwesomeIcon
                                                fontSize="10px"
                                                icon={
                                                    reviewLog.study_rate_change_direction === '2'
                                                        ? ['far', 'hyphen']
                                                        : ['fass', 'triangle']
                                                }
                                                className={cn({
                                                    [styles.sortAscIcon]: reviewLog.study_rate_change_direction === '0',
                                                    [`${styles.sortDescIcon} k-rotate-180`]:
                                                        reviewLog.study_rate_change_direction === '1',
                                                })}
                                            />
                                        </div>
                                    )}
                                    {/* current value */}
                                    {reviewLog.study_rate && (
                                        <div className={cn(editDialogStyles.readonlyPanelText)}>
                                            {reviewLog.study_rate}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* ROW 2: y volume / study site mean */}
                        <div className={cn('k-d-flex k-flex-row k-pt-2 k-pb-1 k-gap-3 k-pb-5')}>
                            {/* R2 COL left: x-axis */}
                            <div
                                className={cn(
                                    '!k-basis-8/12 k-d-flex k-flex-col k-align-items-center',
                                    editDialogStyles.borderRight,
                                )}
                            >
                                {/* y axis column value */}
                                <div className={cn(style.wordBreak, 'k-pr-3')}>{reviewLog.y_axis_column_value}</div>
                                {/* y-axis volume */}
                                <div className={cn(reviewLog.previous_y_volume ? 'k-gap-3' : '', 'k-d-flex')}>
                                    {/* previous y volume (if any) */}
                                    <div className={cn(editDialogStyles.readonlyPanelText)}>
                                        {reviewLog.previous_y_volume}
                                    </div>
                                    {/* direction indicator */}
                                    {reviewLog.y_volume_change_direction && (
                                        <div className={cn(editDialogStyles.readonlyPanelText)}>
                                            <FontAwesomeIcon
                                                fontSize="10px"
                                                icon={
                                                    reviewLog.y_volume_change_direction === '2'
                                                        ? ['far', 'hyphen']
                                                        : ['fass', 'triangle']
                                                }
                                                className={cn({
                                                    [styles.sortAscIcon]: reviewLog.y_volume_change_direction === '0',
                                                    [`${styles.sortDescIcon} k-rotate-180`]:
                                                        reviewLog.y_volume_change_direction === '1',
                                                })}
                                            />
                                        </div>
                                    )}
                                    {/* current y volume */}
                                    <div className={cn(editDialogStyles.readonlyPanelText)}>{reviewLog.y_volume}</div>
                                </div>
                            </div>
                            {/* R2 COL right: study site mean */}
                            <div className="!k-basis-4/12 k-d-flex k-flex-col k-align-items-center">
                                <div>{t('admin:STUDY_SITE_MEAN')}</div>
                                <div className={cn(reviewLog.previous_study_site_mean ? 'k-gap-3' : '', 'k-d-flex')}>
                                    {/* previous value */}
                                    {reviewLog.previous_study_site_mean && (
                                        <div className={cn(editDialogStyles.readonlyPanelText)}>
                                            {reviewLog.previous_study_site_mean}
                                        </div>
                                    )}
                                    {/* direction indicator */}
                                    {reviewLog.study_site_mean_change_direction && (
                                        <div className={cn(editDialogStyles.readonlyPanelText)}>
                                            <FontAwesomeIcon
                                                fontSize="10px"
                                                icon={
                                                    reviewLog.study_site_mean_change_direction === '2'
                                                        ? ['far', 'hyphen']
                                                        : ['fass', 'triangle']
                                                }
                                                className={cn({
                                                    [styles.sortAscIcon]:
                                                        reviewLog.study_site_mean_change_direction === '0',
                                                    [`${styles.sortDescIcon} k-rotate-180`]:
                                                        reviewLog.study_site_mean_change_direction === '1',
                                                })}
                                            />
                                        </div>
                                    )}
                                    {/* current value */}
                                    {reviewLog.study_site_mean && (
                                        <div className={cn(editDialogStyles.readonlyPanelText)}>
                                            {reviewLog.study_site_mean}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className={cn(editDialogStyles.borderBottom)} />

                {/* /study rate / study site mean / x volume / y volume */}
                <div className={cn(style.editSiteObservationInfo, 'k-pt-2 k-pb-1 k-pl-md k-pr-md')}>
                    <div className={cn(style.pipeGap, 'k-shrink-0 k-d-flex k-flex')}>
                        <div className="k-d-flex k-justify-content-left k-align-items-center k-gap-3 !k-basis-11/12">
                            <div className="k-align-items-center k-font-medium">{t('reviewLogDialog.association')}</div>
                            <div className={cn(style.editStudyReviewLogLabel, 'k-d-flex k-align-items-center')}>
                                {reviewLog.association}
                                {reviewLog?.association_active === 'No' && (
                                    <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                                        <InactiveIcon
                                            toolTipText={t('common:inactiveIcon')}
                                            dataTestId="inactive-icon"
                                            tooltipPosition="bottom"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div
                            className={cn(
                                'k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-5/12',
                                {
                                    'k-gap-3': reviewLog.is_scatter_plot === null || reviewLog.is_scatter_plot === 'No',
                                    'k-gap-9': reviewLog.is_scatter_plot === 'Yes',
                                },
                            )}
                        >
                            <div className="k-font-medium">{t('reviewLogDialog.dataset')}</div>
                            <div className={cn(style.editStudyReviewLogLabel)}>{reviewLog.dataset}</div>
                        </div>
                    </div>
                </div>
                <div
                    className={cn(style.editSiteObservationInfo, style.dividerBottom, 'k-pt-1 k-pb-2 k-pl-md k-pr-md')}
                >
                    <div className={cn(style.pipeGap, 'k-shrink-0 k-d-flex k-flex')}>
                        <div className="k-d-flex k-justify-content-left k-gap-12 k-align-items-center !k-basis-11/12">
                            <div className="k-align-items-center k-font-medium ">{t('reviewLogDialog.panel')}</div>
                            <div className={cn(style.editStudyReviewLogLabel, 'k-d-flex k-align-items-center')}>
                                {reviewLog.panel}
                                {reviewLog?.panel_active === 'No' && (
                                    <div className={cn(styles.inactiveIcon, 'k-pl-3')}>
                                        <InactiveIcon
                                            toolTipText={t('common:inactiveIcon')}
                                            dataTestId="inactive-icon"
                                            tooltipPosition="bottom"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center k-gap-3 !k-basis-5/12">
                            <div className="k-font-medium">{t('reviewLogDialog.numberOfActions')}</div>
                            <div className={cn(style.editStudyReviewLogLabel)}>{reviewLog.actions_count}</div>
                        </div>
                    </div>
                </div>

                <form id="edit-study-review-log" className="k-w-full k-pt-sm">
                    <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-sm k-pl-md k-pr-md">
                        <BasicDropDownController
                            label={t('reviewLogDialog.decision')}
                            control={control}
                            name="decision"
                            data-testid="edit-review-log-decision"
                            data={decisionLov}
                            textField="name"
                            groupField="groupedBy"
                            dataItemKey="id"
                            filterable
                            loading={loadingData}
                            allowClear
                            activeField="active"
                            readOnly={isSubmitting}
                        />
                    </div>
                    <div className="k-d-flex k-flex-auto k-justify-content-center k-pl-md k-pr-md">
                        <TextAreaController
                            maxLength={3000}
                            control={control}
                            name="comments"
                            data-testid="edit-review-log-comments"
                            label={t('reviewLogDialog.comments')}
                            style={{
                                height: '326px',
                                overflow: 'auto',
                            }}
                            readOnly={isSubmitting}
                        />
                    </div>
                </form>
            </div>
        </DraggableDialog>
    );
};

export default EditStudyReviewLogDialog;
